import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { getEarlyPayOffers, EarlyPayOffer } from "api/Projects";

/**
 * EARLY-PAYMENT REMINDER (2026-10-08, Jack). The savings offered at quote
 * acceptance only lived in a one-time popup; close it and nothing said the
 * clock was running. This banner shows on the invoice, the accepted quote,
 * the project and the client dashboard until the window closes, the
 * invoice is paid or a discount is applied. One source: the backend
 * /projects/early-pay-offers, which applies the same eligibility rules as
 * the PDFs and payment screens.
 *
 * Filter by invoiceId / quoteId / projectId to show one offer; pass none
 * (dashboard) to list every open offer. `onPay` replaces the default
 * navigation (the invoice page opens its own chooser in place).
 */
const money = (n: number) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const invNo = (n: number) => `GMI-${String(n).padStart(5, "0")}`;

const EarlyPayBanner = ({
  invoiceId,
  quoteId,
  projectId,
  onPay,
}: {
  invoiceId?: string;
  quoteId?: string;
  projectId?: string;
  onPay?: (offer: EarlyPayOffer) => void;
}) => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState<EarlyPayOffer[]>([]);

  useEffect(() => {
    let live = true;
    getEarlyPayOffers().then((all) => {
      if (!live) return;
      setOffers(
        all.filter(
          (o) =>
            (!invoiceId || o.invoiceId === invoiceId) &&
            (!quoteId || o.quoteId === quoteId) &&
            (!projectId || o.projectId === projectId) &&
            moment(o.deadline).isAfter(moment()),
        ),
      );
    });
    return () => {
      live = false;
    };
  }, [invoiceId, quoteId, projectId]);

  if (!offers.length) return null;
  const listMode = !invoiceId && !quoteId && !projectId;

  return (
    <div style={{ maxWidth: 1000, width: "100%", margin: "0 auto 16px auto" }}>
      {offers.map((o) => {
        const end = moment(o.deadline);
        const hoursLeft = end.diff(moment(), "hours");
        const left = hoursLeft < 24 ? `${Math.max(hoursLeft, 1)} hour${hoursLeft === 1 ? "" : "s"} left` : `${Math.ceil(hoursLeft / 24)} days left`;
        return (
          <div
            key={o.invoiceId}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "12px 16px", marginBottom: 8, borderRadius: 8, background: "#EAF6EE", border: "1px solid #B7DFC3", fontFamily: "Poppins" }}
          >
            <div style={{ fontSize: 13, color: "#1F4D2E", lineHeight: 1.5 }}>
              <div style={{ fontWeight: 600 }}>
                {`Save up to ${money(o.fullSave)} — pay by ${end.format("ddd, MMM D [at] h:mm A")} (${left})`}
              </div>
              <div>
                {listMode ? `${invNo(o.invoiceSequence)}${o.projectName ? ` · ${o.projectName}` : ""}: ` : ""}
                {`Pay in full: ${money(o.fullAmount)} (${o.fullPct}% off) · or 50% deposit now: ${money(o.depositAmount)} (${o.depositPct}% off the total).`}
              </div>
            </div>
            <button
              type="button"
              onClick={() => (onPay ? onPay(o) : navigate(`/invoices/${o.invoiceId}?pay=1`))}
              style={{ background: "#276749", color: "#fff", border: 0, borderRadius: 6, padding: "8px 16px", fontFamily: "Poppins", fontSize: 13, fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap" }}
            >
              Pay now
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default EarlyPayBanner;
